import React, { useMemo } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  format,
  parseISO
} from 'date-fns';
import EventPill, { EventPillStack } from './EventPill';

/**
 * MonthView - Calendar month grid with events
 *
 * Features:
 * - 7-column grid (Sun-Sat)
 * - Shows days from adjacent months (grayed out)
 * - Today highlighting
 * - Events displayed as pills
 * - Click on date to quick-add
 * - Click on event to open slideout
 * - Overflow indicator for dates with many events
 */

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_OF_WEEK_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MAX_EVENTS_PER_DAY = 3; // Show "+X more" after this many

const MonthView = ({
  currentDate,
  events = [],
  onDateClick,
  onEventClick,
  onShowMore,
  onNavigateToMonth,
  loading
}) => {
  // Generate calendar days (full grid including adjacent month days)
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  // Group events by date for efficient lookup
  const eventsByDate = useMemo(() => {
    const grouped = {};

    events.forEach(event => {
      if (!event.startTime) return;

      try {
        const eventDate = parseISO(event.startTime);
        const dateKey = format(eventDate, 'yyyy-MM-dd');

        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(event);
      } catch (err) {
        console.warn('Invalid event date:', event.startTime);
      }
    });

    // Sort events within each day by time
    Object.keys(grouped).forEach(dateKey => {
      grouped[dateKey].sort((a, b) => {
        if (a.allDay && !b.allDay) return -1;
        if (!a.allDay && b.allDay) return 1;
        return new Date(a.startTime) - new Date(b.startTime);
      });
    });

    return grouped;
  }, [events]);

  // Handle date cell click
  // If clicking a day from an adjacent month, navigate to that month
  // Otherwise, open quick-add popover for the current month day
  const handleDateClick = (date, e) => {
    const isCurrentMonth = isSameMonth(date, currentDate);

    if (!isCurrentMonth && onNavigateToMonth) {
      // Navigate to the clicked month
      onNavigateToMonth(date);
    } else if (onDateClick) {
      // Open quick-add popover
      const rect = e.currentTarget.getBoundingClientRect();
      onDateClick(date, {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      });
    }
  };

  // Handle event click
  const handleEventClick = (event) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };

  // Handle "show more" click - switches to Week view for that day
  const handleShowMore = (date, e) => {
    e.stopPropagation();
    if (onShowMore) {
      onShowMore(date);
    }
  };

  // Render day cell
  const renderDayCell = (day) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    const dayEvents = eventsByDate[dateKey] || [];
    const isCurrentMonth = isSameMonth(day, currentDate);
    const isCurrentDay = isToday(day);
    const hasOverflow = dayEvents.length > MAX_EVENTS_PER_DAY;
    const visibleEvents = hasOverflow ? dayEvents.slice(0, MAX_EVENTS_PER_DAY) : dayEvents;
    const overflowCount = dayEvents.length - MAX_EVENTS_PER_DAY;

    return (
      <div
        key={day.toISOString()}
        onClick={(e) => handleDateClick(day, e)}
        className={`
          min-h-[60px] sm:min-h-[100px] p-0.5 sm:p-1 border-b border-r border-gray-200
          cursor-pointer hover:bg-gray-50 transition-colors
          ${!isCurrentMonth ? 'bg-gray-50' : 'bg-white'}
        `}
      >
        {/* Day number */}
        <div className="flex justify-end mb-0.5 sm:mb-1">
          <span
            className={`
              text-xs sm:text-sm w-5 h-5 sm:w-7 sm:h-7 flex items-center justify-center rounded-full
              ${isCurrentDay
                ? 'bg-yellow-400 text-gray-900 font-semibold'
                : isCurrentMonth
                  ? 'text-gray-900'
                  : 'text-gray-400'
              }
            `}
          >
            {format(day, 'd')}
          </span>
        </div>

        {/* Events */}
        <div className="space-y-0.5">
          {visibleEvents.map(event => (
            <EventPill
              key={event.id}
              event={event}
              onClick={handleEventClick}
              size="sm"
              showTime={true}
            />
          ))}

          {/* Overflow indicator */}
          {hasOverflow && (
            <EventPillStack
              count={overflowCount}
              onClick={(e) => handleShowMore(day, e)}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="select-none">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {DAYS_OF_WEEK.map((day, index) => (
          <div
            key={day}
            className="py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{DAYS_OF_WEEK_SHORT[index]}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 border-l border-gray-200">
        {loading ? (
          // Loading skeleton
          Array.from({ length: 35 }).map((_, i) => (
            <div
              key={i}
              className="min-h-[100px] p-1 border-b border-r border-gray-200 bg-white animate-pulse"
            >
              <div className="flex justify-end mb-1">
                <div className="w-7 h-7 bg-gray-200 rounded-full" />
              </div>
              <div className="space-y-1">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))
        ) : (
          calendarDays.map(day => renderDayCell(day))
        )}
      </div>
    </div>
  );
};

export default MonthView;
